import React, { useState, useEffect, useCallback } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';
import { initiateStravaOAuth, disconnectStrava, syncStravaActivities } from '../../../services/stravaService';

// ── Strava Client ID check ────────────────────────────────────────────────────
// Mirrors the logic in stravaService.js so the UI banner reflects the real state.
const HARDCODED_STRAVA_CLIENT_ID = ''; // ← same value as in stravaService.js
const _envClientId = import.meta.env?.VITE_STRAVA_CLIENT_ID;
const STRAVA_CLIENT_ID =
  HARDCODED_STRAVA_CLIENT_ID ||
  (_envClientId && _envClientId !== 'your-strava-client-id-here' ? _envClientId : '');
const IS_STRAVA_CONFIGURED = !!STRAVA_CLIENT_ID;

const PROVIDERS = [
  {
    id: 'strava',
    name: 'Strava',
    description: 'Import runs, rides and workouts',
    icon: 'Activity',
    color: 'text-orange-500',
    bgColor: 'bg-orange-50 dark:bg-orange-950/30',
    borderColor: 'border-orange-200 dark:border-orange-800',
    oauthEnabled: true,
  },
  {
    id: 'garmin',
    name: 'Garmin',
    description: 'Sync activities from Garmin Connect',
    icon: 'Watch',
    color: 'text-blue-500',
    bgColor: 'bg-blue-50 dark:bg-blue-950/30',
    borderColor: 'border-blue-200 dark:border-blue-800',
    oauthEnabled: false,
  },
  {
    id: 'whoop',
    name: 'WHOOP',
    description: 'Sync recovery and strain data',
    icon: 'Heart',
    color: 'text-red-500',
    bgColor: 'bg-red-50 dark:bg-red-950/30',
    borderColor: 'border-red-200 dark:border-red-800',
    oauthEnabled: false,
  },
  {
    id: 'oura',
    name: 'Oura Ring',
    description: 'Import sleep and readiness scores',
    icon: 'Moon',
    color: 'text-purple-500',
    bgColor: 'bg-purple-50 dark:bg-purple-950/30',
    borderColor: 'border-purple-200 dark:border-blue-800',
    oauthEnabled: false,
  },
  {
    id: 'fitbit',
    name: 'Fitbit',
    description: 'Sync steps, heart rate and sleep',
    icon: 'Zap',
    color: 'text-teal-500',
    bgColor: 'bg-teal-50 dark:bg-teal-950/30',
    borderColor: 'border-teal-200 dark:border-teal-800',
    oauthEnabled: false,
  },
];

const ConnectedDevicesSection = () => {
  const { user } = useAuth();
  const [connections, setConnections] = useState({});
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState({});
  const [syncLoading, setSyncLoading] = useState({});
  const [connectingStrava, setConnectingStrava] = useState(false);
  const [providerErrors, setProviderErrors] = useState({});
  const [syncMessage, setSyncMessage] = useState(null);
  const [stravaWarningDismissed, setStravaWarningDismissed] = useState(false);

  const clearProviderError = (providerId) => {
    setProviderErrors((prev) => {
      const next = { ...prev };
      delete next?.[providerId];
      return next;
    });
  };

  const setProviderError = (providerId, message) => {
    setProviderErrors((prev) => ({ ...prev, [providerId]: message }));
  };

  const loadConnections = useCallback(async () => {
    if (!user?.id) return;
    try {
      setLoading(true);
      const { data, error: fetchError } = await supabase
        ?.from('fitness_connections')
        ?.select('*')
        ?.eq('user_id', user?.id);

      if (fetchError) throw fetchError;

      const connectionsMap = {};
      data?.forEach((conn) => {
        connectionsMap[conn.provider] = conn;
      });
      setConnections(connectionsMap);
    } catch (err) {
      setProviderError('_global', err?.message || 'Failed to load connections');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    loadConnections();
  }, [loadConnections]);

  // ── Strava OAuth Connect ────────────────────────────────────────────────────
  const handleStravaConnect = () => {
    if (!user?.id) return;
    clearProviderError('strava');
    try {
      setConnectingStrava(true);
      initiateStravaOAuth(user?.id);
      // Note: page will redirect, so setConnectingStrava(false) is not needed
    } catch (err) {
      setConnectingStrava(false);
      setProviderError('strava', err?.message || 'Failed to initiate Strava connection');
    }
  };

  // ── Strava Disconnect ───────────────────────────────────────────────────────
  const handleStravaDisconnect = async () => {
    try {
      setActionLoading((prev) => ({ ...prev, strava: true }));
      clearProviderError('strava');
      await disconnectStrava(user?.id);
      await loadConnections();
    } catch (err) {
      setProviderError('strava', err?.message || 'Failed to disconnect Strava');
    } finally {
      setActionLoading((prev) => ({ ...prev, strava: false }));
    }
  };

  // ── Strava Sync ─────────────────────────────────────────────────────────────
  const handleStravaSync = async () => {
    try {
      setSyncLoading((prev) => ({ ...prev, strava: true }));
      setSyncMessage(null);
      clearProviderError('strava');
      const result = await syncStravaActivities(user?.id);
      const count = result?.total_activities_synced ?? result?.results?.[0]?.activities_synced ?? 0;
      setSyncMessage(`Synced ${count} new activit${count === 1 ? 'y' : 'ies'} from Strava`);
      await loadConnections();
      setTimeout(() => setSyncMessage(null), 4000);
    } catch (err) {
      setProviderError('strava', err?.message || 'Failed to sync Strava activities');
    } finally {
      setSyncLoading((prev) => ({ ...prev, strava: false }));
    }
  };

  // ── Generic placeholder connect (non-OAuth providers) ──────────────────────
  const handlePlaceholderConnect = async (providerId) => {
    try {
      setActionLoading((prev) => ({ ...prev, [providerId]: true }));
      clearProviderError(providerId);
      const { error: insertError } = await supabase?.from('fitness_connections')?.insert({
        user_id: user?.id,
        provider: providerId,
        provider_user_id: null,
        access_token: null,
        refresh_token: null,
      });
      if (insertError) throw insertError;
      await loadConnections();
    } catch (err) {
      setProviderError(providerId, err?.message || `Failed to connect ${providerId}`);
    } finally {
      setActionLoading((prev) => ({ ...prev, [providerId]: false }));
    }
  };

  const handleGenericDisconnect = async (providerId) => {
    try {
      setActionLoading((prev) => ({ ...prev, [providerId]: true }));
      clearProviderError(providerId);
      const { error: deleteError } = await supabase
        ?.from('fitness_connections')
        ?.delete()
        ?.eq('user_id', user?.id)
        ?.eq('provider', providerId);
      if (deleteError) throw deleteError;
      await loadConnections();
    } catch (err) {
      setProviderError(providerId, err?.message || `Failed to disconnect ${providerId}`);
    } finally {
      setActionLoading((prev) => ({ ...prev, [providerId]: false }));
    }
  };

  const connectedCount = Object.keys(connections)?.length;

  const formatLastSynced = (isoString) => {
    if (!isoString) return null;
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date?.toLocaleDateString();
  };

  return (
    <div className="bg-surface border border-border rounded-2xl p-6">
      {/* Section Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
          <Icon name="Bluetooth" size={20} className="text-primary" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-foreground">Connected Devices</h2>
          <p className="text-sm text-muted-foreground">
            {connectedCount > 0
              ? `${connectedCount} of ${PROVIDERS?.length} providers connected`
              : 'Connect your fitness trackers to sync data automatically'}
          </p>
        </div>
      </div>
      {/* Global error banner */}
      {providerErrors?._global && (
        <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <Icon name="AlertCircle" size={16} className="text-destructive flex-shrink-0" />
            <p className="text-sm text-destructive truncate">{providerErrors?._global}</p>
          </div>
          <button
            onClick={() => { clearProviderError('_global'); loadConnections(); }}
            className="text-xs text-destructive underline flex-shrink-0 hover:no-underline"
          >
            Retry
          </button>
        </div>
      )}
      {/* Strava not configured warning */}
      {!IS_STRAVA_CONFIGURED && !stravaWarningDismissed && (
        <div className="mb-4 p-4 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-xl">
          <div className="flex items-start justify-between gap-2 mb-2">
            <div className="flex items-start gap-2">
              <Icon name="AlertTriangle" size={16} className="text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm font-medium text-amber-800 dark:text-amber-300">Strava not configured</p>
            </div>
            <button
              onClick={() => setStravaWarningDismissed(true)}
              className="text-amber-600 dark:text-amber-400 hover:text-amber-800 dark:hover:text-amber-200 flex-shrink-0"
              title="Dismiss"
            >
              <Icon name="X" size={14} />
            </button>
          </div>
          <p className="text-xs text-amber-700 dark:text-amber-400 mb-1">
            To enable Strava OAuth, complete these steps:
          </p>
          <p className="text-xs text-amber-600 dark:text-amber-500 mb-3 font-mono bg-amber-100 dark:bg-amber-900/50 px-2 py-1 rounded">
            Current VITE_STRAVA_CLIENT_ID: "{_envClientId || '(not set)'}"
          </p>
          <ol className="text-xs text-amber-700 dark:text-amber-400 space-y-1.5 list-none">
            <li className="flex items-start gap-2">
              <span className="w-4 h-4 rounded-full bg-amber-200 dark:bg-amber-800 text-amber-800 dark:text-amber-300 flex items-center justify-center text-[10px] font-bold flex-shrink-0 mt-0.5">1</span>
              <span>Create a Strava app at <strong>strava.com/settings/api</strong> and set <strong>Authorization Callback Domain</strong> to: <code className="bg-amber-100 dark:bg-amber-900 px-1 rounded font-mono">{window.location?.hostname}</code></span>
            </li>
            <li className="flex items-start gap-2">
              <span className="w-4 h-4 rounded-full bg-amber-200 dark:bg-amber-800 text-amber-800 dark:text-amber-300 flex items-center justify-center text-[10px] font-bold flex-shrink-0 mt-0.5">2</span>
              <span>Replace <code className="bg-amber-100 dark:bg-amber-900 px-1 rounded font-mono">your-strava-client-id-here</code> in <strong>.env</strong> with your actual numeric Client ID (e.g. <code className="bg-amber-100 dark:bg-amber-900 px-1 rounded font-mono">VITE_STRAVA_CLIENT_ID=123456</code>)</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="w-4 h-4 rounded-full bg-amber-200 dark:bg-amber-800 text-amber-800 dark:text-amber-300 flex items-center justify-center text-[10px] font-bold flex-shrink-0 mt-0.5">3</span>
              <span>Add <code className="bg-amber-100 dark:bg-amber-900 px-1 rounded font-mono">STRAVA_CLIENT_ID</code> and <code className="bg-amber-100 dark:bg-amber-900 px-1 rounded font-mono">STRAVA_CLIENT_SECRET</code> in Supabase → Edge Functions → Secrets</span>
            </li>
          </ol>
        </div>
      )}
      {/* Sync Success Banner */}
      {syncMessage && (
        <div className="mb-4 p-3 bg-success/10 border border-success/20 rounded-lg flex items-center gap-2">
          <Icon name="CheckCircle2" size={16} className="text-success flex-shrink-0" />
          <p className="text-sm text-success">{syncMessage}</p>
        </div>
      )}
      {/* Connecting to Strava overlay message */}
      {connectingStrava && (
        <div className="mb-4 p-3 bg-orange-50 dark:bg-orange-950/30 border border-orange-200 dark:border-orange-800 rounded-lg flex items-center gap-2">
          <div className="w-4 h-4 border-2 border-orange-500 border-t-transparent rounded-full animate-spin flex-shrink-0" />
          <p className="text-sm text-orange-700 dark:text-orange-300">Redirecting to Strava for authorization…</p>
        </div>
      )}
      {/* Provider List */}
      {loading ? (
        <div className="space-y-3">
          {PROVIDERS?.map((p) => (
            <div key={p?.id} className="h-20 bg-muted/40 rounded-xl animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="space-y-3">
          {PROVIDERS?.map((provider) => {
            const connection = connections?.[provider?.id];
            const isConnected = !!connection;
            const isActioning = !!actionLoading?.[provider?.id];
            const isSyncing = !!syncLoading?.[provider?.id];
            const isConnecting = provider?.id === 'strava' && connectingStrava;
            const providerError = providerErrors?.[provider?.id];
            const athleteData = connection?.athlete_data;
            const lastSynced = connection?.last_synced_at;
            const athleteName = athleteData
              ? `${athleteData?.firstname || ''} ${athleteData?.lastname || ''}`?.trim()
              : null;
            const athleteAvatar = athleteData?.profile || null;

            return (
              <div
                key={provider?.id}
                className={`rounded-xl border transition-all ${
                  isConnected
                    ? `${provider?.bgColor} ${provider?.borderColor}`
                    : providerError
                    ? 'bg-destructive/5 border-destructive/30' :'bg-muted/20 border-border'
                }`}
              >
                {/* Main Row */}
                <div className="flex items-center justify-between p-4">
                  {/* Provider Info */}
                  <div className="flex items-center gap-3 min-w-0">
                    {/* Avatar or Icon */}
                    {isConnected && athleteAvatar ? (
                      <img
                        src={athleteAvatar}
                        alt={`${athleteName || provider?.name} avatar`}
                        className="w-10 h-10 rounded-lg object-cover flex-shrink-0 border border-white/30"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div
                      className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        isConnected && athleteAvatar ? 'hidden' : ''
                      } ${isConnected ? 'bg-white/70 dark:bg-black/20' : 'bg-muted/50'}`}
                    >
                      <Icon
                        name={provider?.icon}
                        size={20}
                        className={isConnected ? provider?.color : 'text-muted-foreground'}
                      />
                    </div>

                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-foreground text-sm">{provider?.name}</span>
                        {isConnected && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-success/15 text-success text-xs font-medium">
                            <span className="w-1.5 h-1.5 rounded-full bg-success inline-block" />
                            Connected
                          </span>
                        )}
                        {isConnecting && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-orange-100 dark:bg-orange-950/40 text-orange-600 dark:text-orange-400 text-xs font-medium">
                            <div className="w-1.5 h-1.5 border border-orange-500 border-t-transparent rounded-full animate-spin" />
                            Connecting…
                          </span>
                        )}
                      </div>

                      {/* Athlete name or description */}
                      <p className="text-xs text-muted-foreground truncate">
                        {isConnected && athleteName
                          ? athleteName
                          : isConnected && connection?.created_at
                          ? `Since ${new Date(connection?.created_at)?.toLocaleDateString()}`
                          : provider?.description}
                      </p>

                      {/* Last synced */}
                      {isConnected && lastSynced && (
                        <p className="text-xs text-muted-foreground/70 mt-0.5 flex items-center gap-1">
                          <Icon name="RefreshCw" size={10} />
                          Synced {formatLastSynced(lastSynced)}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                    {isConnected ? (
                      <>
                        {/* Sync button — only for Strava (OAuth-enabled) */}
                        {provider?.oauthEnabled && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={provider?.id === 'strava' ? handleStravaSync : undefined}
                            disabled={isSyncing || isActioning}
                            className="text-orange-500 border-orange-300 hover:bg-orange-50 dark:hover:bg-orange-950/40 text-xs px-3 py-1.5 h-auto"
                            title="Sync activities now"
                          >
                            {isSyncing ? (
                              <span className="flex items-center gap-1.5">
                                <Icon name="Loader2" size={14} className="animate-spin" />
                                <span>Syncing…</span>
                              </span>
                            ) : (
                              <span className="flex items-center gap-1.5">
                                <Icon name="RefreshCw" size={14} />
                                <span>Sync</span>
                              </span>
                            )}
                          </Button>
                        )}

                        {/* Disconnect button */}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={
                            provider?.id === 'strava'
                              ? handleStravaDisconnect
                              : () => handleGenericDisconnect(provider?.id)
                          }
                          disabled={isActioning || isSyncing}
                          className="text-destructive border-destructive/30 hover:bg-destructive/10 hover:border-destructive/50 text-xs px-3 py-1.5 h-auto"
                        >
                          {isActioning ? (
                            <span className="flex items-center gap-1.5">
                              <Icon name="Loader2" size={14} className="animate-spin" />
                              <span>Disconnecting…</span>
                            </span>
                          ) : (
                            'Disconnect'
                          )}
                        </Button>
                      </>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={
                          provider?.id === 'strava'
                            ? handleStravaConnect
                            : () => handlePlaceholderConnect(provider?.id)
                        }
                        disabled={isActioning || isConnecting || (!IS_STRAVA_CONFIGURED && provider?.id === 'strava')}
                        className={`text-xs px-3 py-1.5 h-auto ${
                          provider?.id === 'strava' ?'text-orange-500 border-orange-300 hover:bg-orange-50 dark:hover:bg-orange-950/40 font-semibold disabled:opacity-50' :'text-primary border-primary/30 hover:bg-primary/10 hover:border-primary/50'
                        }`}
                      >
                        {isConnecting ? (
                          <span className="flex items-center gap-1.5">
                            <Icon name="Loader2" size={14} className="animate-spin" />
                            <span>Connecting…</span>
                          </span>
                        ) : isActioning ? (
                          <Icon name="Loader2" size={14} className="animate-spin" />
                        ) : provider?.id === 'strava' ? (
                          <span className="flex items-center gap-1.5">
                            <Icon name="Link" size={13} />
                            Connect Strava
                          </span>
                        ) : (
                          'Connect'
                        )}
                      </Button>
                    )}
                  </div>
                </div>

                {/* Per-provider error with retry */}
                {providerError && (
                  <div className="mx-4 mb-3 p-2.5 bg-destructive/10 border border-destructive/20 rounded-lg">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-start gap-1.5 min-w-0">
                        <Icon name="AlertCircle" size={14} className="text-destructive flex-shrink-0 mt-0.5" />
                        <p className="text-xs text-destructive leading-relaxed">{providerError}</p>
                      </div>
                      <button
                        onClick={() => {
                          clearProviderError(provider?.id);
                          if (provider?.id === 'strava' && !isConnected) {
                            handleStravaConnect();
                          } else if (provider?.id === 'strava' && isConnected) {
                            handleStravaSync();
                          }
                        }}
                        className="text-xs text-destructive underline flex-shrink-0 hover:no-underline font-medium"
                      >
                        Retry
                      </button>
                    </div>
                  </div>
                )}

                {/* Strava OAuth badge — shown only when not connected and no error */}
                {provider?.id === 'strava' && !isConnected && !providerError && (
                  <div className="px-4 pb-3 flex items-center gap-1.5">
                    <Icon name="Shield" size={12} className="text-orange-400" />
                    <span className="text-xs text-muted-foreground">
                      Secure OAuth 2.0 · Scopes: activity:read_all, profile:read_all
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      {/* Footer Note */}
      <p className="mt-4 text-xs text-muted-foreground flex items-start gap-1.5">
        <Icon name="Info" size={13} className="flex-shrink-0 mt-0.5" />
        Strava uses OAuth 2.0 for secure authentication. Other providers will be enabled in future
        updates.
      </p>
    </div>
  );
};

export default ConnectedDevicesSection;
